// The accounting system the ledger export goes to (technical design §8.2) and the export itself
// (QA-291), as words and state both designs draw: Riverstone's Connections and LedgerExport panels and
// Blueprint's AccountingSystem (BL-189). Which company is connected, coming back from the provider's
// consent page (the hash says how it went, read once), the connect route, and what an export's
// overview holds. No styles and no parts, so a page that imports it loads no stylesheet.

import { refusalOf, RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { monthName, monthOf, previousMonth } from './model.ts';

export type Target = 'quickbooks' | 'xero';
export const LABEL: Record<Target, string> = { quickbooks: 'QuickBooks Online', xero: 'Xero' };
export const TARGETS: readonly Target[] = ['quickbooks', 'xero'];

export interface Connection {
  target: Target;
  fake: boolean;
  companyName: string | null;
  connectedBy: string;
  connectedAt: string;
}

/** Back from the provider: #ledger=<target>_<connected|declined|failed>, read once as the page loads
 *  (no location where the catalogue loads the Ledger page's stories outside a browser). */
const RETURNED = /^#ledger=(quickbooks|xero)_(connected|declined|failed|one_org)$/.exec(
  typeof location === 'undefined' ? '' : location.hash,
);
let untold = RETURNED !== null;
/** This page load came back from the provider (the office opens on the books). */
export const CAME_BACK_FROM_LEDGER = RETURNED !== null;
/** Until the panel has said how it went: the books open on it. */
export const ledgerReturnUntold = () => untold;

const RETURN_TEXT = {
  connected: (t: Target) => `${LABEL[t]} is connected.`,
  declined: (t: Target) => `${LABEL[t]} was not connected: the approval was cancelled.`,
  failed: (t: Target) => `${LABEL[t]} did not accept the connection. Try again.`,
  one_org: (t: Target) =>
    `${LABEL[t]} wasn’t connected: choose exactly one organisation on its page, then connect again.`,
};

/** How connecting went, once: the words for the toast, and the address back to the page's own. */
export function takeLedgerReturn(): string | null {
  if (!untold || !RETURNED) return null;
  untold = false;
  history.replaceState(null, '', location.pathname + location.search);
  return RETURN_TEXT[RETURNED[2] as keyof typeof RETURN_TEXT](RETURNED[1] as Target);
}

/** The connect route is plain HTTP (it answers with the provider's link), with the CSRF header. */
export async function connectUrl(target: Target): Promise<string> {
  const csrf = (await client.session())?.csrf;
  const res = await fetch(`/integrations/${target}/connect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(csrf ? { 'x-flow-csrf': csrf } : {}) },
    body: '{}',
  });
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean;
    result?: { url: string };
    error?: unknown;
  } | null;
  // A refusal says what to do, ours or the way there's (a 413, a proxy's 502), as the client does.
  if (!res.ok || !body?.ok || !body.result) throw new RpcError(res.status, refusalOf(res.status, body));
  return body.result.url;
}

/** exports.overview: our accounts and their match, their chart once read, last month, the last runs. */
export interface Overview {
  accounts: { accountId: string; code: string; name: string; externalAccount: string | null }[];
  unmapped: number;
  canEdit: boolean;
  /** Their chart from the last Match by name, or null until then. */
  theirChart: { externalId: string; code: string | null; name: string }[] | null;
  lastMonth: { from: string; to: string; closed: boolean };
  runs: {
    exportId: string;
    target: Target;
    periodStart: string;
    periodEnd: string;
    mode: 'summary' | 'detail';
    status: 'pending' | 'done' | 'failed';
    sentCount: number;
    skippedCount: number;
    error: string | null;
    runAt: string;
  }[];
}

export const MODE: Record<'summary' | 'detail', string> = {
  detail: 'Every journal entry',
  summary: 'Daily summaries per account',
};

/** The last four months, this one to date. */
export function exportMonths(today: string): { value: string; label: string; from: string; to: string }[] {
  const out = [];
  let [from, to] = monthOf(today);
  for (let i = 0; i < 4; i++) {
    out.push({
      value: from,
      label: i === 0 ? `${monthName(from)} (to date)` : monthName(from),
      from,
      to: i === 0 ? today : to,
    });
    [from, to] = previousMonth(from);
  }
  return out;
}

/** Their account's name in our words: its code, then its name. */
export const theirLabel = (x: { code: string | null; name: string }) =>
  `${x.code ? `${x.code} ` : ''}${x.name}`;
